import type { CartItem } from "../store/useStore";
import { uuid } from "./uuid";

// On the custom domain, /api/* is routed to the worker. On *.pages.dev (no
// route), fall back to the worker's direct URL so dev/preview still works.
// Forks set VITE_WORKER_URL in .env (see docs/FORKING.md).
const BASE = typeof window !== "undefined" && window.location.hostname.endsWith(".pages.dev")
  ? (import.meta.env.VITE_WORKER_URL || "https://vicaza-pos-api.hdvictoriamx.workers.dev")
  : "";

export type CheckoutResponse = {
  order_id: string;
  mp_order_id: string;
};

export async function createCheckout(args: {
  cart: CartItem[];
  customerName: string;
  language: string;
}): Promise<CheckoutResponse> {
  const res = await fetch(`${BASE}/api/checkout`, {
    method: "POST",
    // Fresh key per logical checkout attempt — replays of the same request
    // must not create a second MP terminal order.
    headers: { "Content-Type": "application/json", "X-Idempotency-Key": uuid() },
    body: JSON.stringify({
      items: args.cart.map((i) => ({ sku: i.sku, qty: i.qty })),
      customer_name: args.customerName,
      language: args.language,
    }),
  });
  if (!res.ok) throw new Error("checkout failed: " + res.status);
  return res.json();
}

export type OrderStatus = {
  status: "created" | "at_terminal" | "processed" | "canceled" | "expired" | "rejected";
  status_detail?: string;
  payment?: { paid_amount: string; payment_method: { id: string }; card?: { last_digits: string } };
};

export async function getOrderStatus(orderId: string): Promise<OrderStatus> {
  const res = await fetch(`${BASE}/api/orders/${orderId}`);
  if (!res.ok) throw new Error("status fetch failed: " + res.status);
  return res.json();
}

export async function cancelOrder(orderId: string): Promise<void> {
  await fetch(`${BASE}/api/orders/${orderId}/cancel`, { method: "POST" });
}

export type Upsell = { sku: string; copy: string } | null;
export async function getUpsell(args: { cart: CartItem[]; language: string }): Promise<Upsell> {
  const res = await fetch(`${BASE}/api/upsell`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: args.cart.map((i) => ({ sku: i.sku, qty: i.qty })),
      language: args.language,
    }),
  });
  if (!res.ok) return null;
  return res.json();
}
